import { readable, writable, derived, get } from "svelte/store";
import api from "./api";
import { transformStringToInputObject, deepcopy, toObject, sortAlphabetically } from "./utils";


export const debug = writable(false);

// ///////////////////////////////////////////////////////////
// routing
// ///////////////////////////////////////////////////////////

export const routes = writable([]);

export const routesById = derived(routes, (routes) => toObject(routes));

// ///////////////////////////////////////////////////////////
// authentication
// ///////////////////////////////////////////////////////////

export const user = writable(null);

export const initialUserSettings = writable(null);

export const loggedin = derived(user, (user) => !!user);

// ///////////////////////////////////////////////////////////
// data
// ///////////////////////////////////////////////////////////
export const setupComplete = writable(false);

export const fetchOrganizations = async () => {
	// get organizations and only take which ones to user belongs to
	const data = (await api.get("organisations/")).filter((o) =>
		get(initialUserSettings).organisations.includes(o.organisation_id)
	);

	data.forEach((o) => {
		o.modified = {};
		o.font_theme = transformStringToInputObject(o.font_theme);
		o.preview_font_theme = transformStringToInputObject(o.preview_font_theme);
		o.languages_full = deepcopy(o.languages).map(l => get(languagesById)[l]);
		o.languages_names = deepcopy(o.languages_full).map(l => (l.name));
	});

	organizations.set(data);
};

export const fetchProjects = async () => {
	const organization = get(selectedOrganization)

	if (!organization) {
		projects.set([]);
		return;
	}

	const data = await api.get(`projects/?organisation=${organization}`);

	/*	
	// sort alphabetically and so that regular dataviz updates are always last
	data.sort((a, b) => sortAlphabetically(a, b, "name"));
	data.sort((a, b) => {
		const aIsRdu = a.name.includes("Regular Dataviz Update")
		const bIsRdu = b.name.includes("Regular Dataviz Update")
		return aIsRdu - bIsRdu;
	})
	*/
	projects.set(data);

}

export const fetchOrganizationProjectSettings = async () => {
	const organization = get(selectedOrganization);
	const project = get(selectedProject);
	const projectObj = get(selectedProjectObj);

	if (!organization || !project) {
		organizationProjectSettings.set(null);
		return;
	}

	let data = await api.get(`organisationProjectSettings/${organization}__${project}/`).catch((error) => null);

	// if the organization project settings don't exist, create them
	if (!data)
		data = await api.post(`organisationProjectSettings/`, {
			organisation: organization,
			project: project,
			color_theme: projectObj.default_color_theme,
		});

	data.font_theme = transformStringToInputObject(data.font_theme);
	data.color_theme = transformStringToInputObject(data.color_theme);

	organizationProjectSettings.set(data);
};

export const fetchProjectColorThemes = async () => {
	const project = get(selectedProject);

	if (!project) return;

	let data = await api.get(`projects/${project}/colorthemes/`);

	colorthemes.set(data);
};

export const organizations = writable([]);
export const organizationsById = derived(organizations, (organizations) => toObject(organizations, "organisation_id"));

export const projects = writable([]);
export const projectsById = derived(projects, (projects) => toObject(projects, "project_id"));

export const graphics = writable([]);
export const graphicsById = derived(graphics, (graphics) => toObject(graphics, "graphic_id"));

export const getInstance = (id) => {
	let instance;
	get(graphics).forEach((g) => {
		const tmp = g.instances.find((i) => i.graphicinstance_id === id);
		if (tmp) instance = tmp;
	});
	return instance;
};

export const graphicInstances = writable([]);
export const graphicInstancesById = derived(graphicInstances, (graphicInstances) =>
	toObject(graphicInstances, "graphicinstance_id")
);

export const languages = writable([]);
export const languagesById = derived(languages, (languages) => toObject(languages, "code"));

export const fontthemes = writable([]);
export const fontthemesById = derived(fontthemes, (fontthemes) => toObject(fontthemes, "fonttheme_id"));

export const colorthemeDefinitions = writable([]);
export const colorthemeDefinitionsById = derived(colorthemeDefinitions, (colorthemeDefinitions) =>
	toObject(colorthemeDefinitions, "id")
);

export const colorthemes = writable([]);
export const colorthemesById = derived(colorthemes, (colorthemes) => toObject(colorthemes, "colortheme_id"));

// ///////////////////////////////////////////////////////////
// embed
// ///////////////////////////////////////////////////////////
export const embedInfo = writable();

// ///////////////////////////////////////////////////////////
// selected options
// ///////////////////////////////////////////////////////////

export const selectedProject = writable();
export const selectedProjectObj = derived([selectedProject, projectsById], ([selectedProject, projectsById]) =>
	selectedProject ? projectsById[selectedProject] : undefined
);

export const selectedOrganization = writable();
export const selectedOrganizationObj = derived(
	[selectedOrganization, organizationsById],
	([selectedOrganization, organizationsById]) =>
		selectedOrganization ? organizationsById[selectedOrganization] : undefined
);
export const selectedOrganizationLanguages = derived(selectedOrganizationObj, selectedOrganizationObj => selectedOrganizationObj ? selectedOrganizationObj.languages : [])
export const selectedOrganizationLanguagesNames = derived(selectedOrganizationObj, selectedOrganizationObj => selectedOrganizationObj ? selectedOrganizationObj.languages_names : [])
export const selectedOrganizationLanguagesFull = derived(selectedOrganizationObj, selectedOrganizationObj => selectedOrganizationObj ? selectedOrganizationObj.languages_full : [])

export const organizationProjectSettings = writable();

// custom writable store that is never undefined or null
const writableNotNull = (initial) => {
	let saved = initial;

	const { subscribe, set, update } = writable(initial);

	return {
		subscribe,
		update,
		set: (value) => {
			saved = value || saved;
			set(saved)
		}
	};
}
export const selectedLanguage = writableNotNull();


export const selectedLanguageName = derived(([selectedLanguage, languagesById]), ([selectedLanguage, languagesById]) => languagesById[selectedLanguage] ? languagesById[selectedLanguage].name : undefined);
